import type { PostTablesLabels200DataItem } from '@ipfabric/api-client/client/schemas/postTablesLabels200DataItem';
import { Button } from '@ipfabric/ui-components/Button/Button';
import { Tooltip } from '@ipfabric/ui-components/Tooltip/Tooltip';
import { useStableCallback } from '@ipfabric/ui-components/hooks/useStableCallback';
import type { Filter } from '@ipfabric/utils';
import type { RowSelectionFilter } from 'components/ApiTable/model/ApiTableModel';
import { applyExternalFilters } from 'components/ApiTable/model/commands/applyExternalFilters';
import { reloadTableData } from 'components/ApiTable/model/commands/reloadTableData';
import { useApiTablePage } from 'components/ApiTablePage/useApiTablePage';
import type {
  TableActionsItem,
  TableBulkAction,
  TableBulkActionClickEvent,
  TableRowIdValue,
} from 'components/Table/model/TableModel';
import { useSimpleConfirmDialog } from 'containers/ConfirmDialog/useSimpleConfirmDialog';
import { useMemo, useRef, useState } from 'react';
import { useTableEndpoint } from 'utils/table/useTableEndpoint';
import type { DiscoveryDataEnrichmentLink } from '../discoveryDataEnrichmentLink';
import { useOpenDiscoveryDataEnrichment } from '../useOpenDiscoveryDataEnrichment';
import { readTableCell } from './assignedLabelsRow';
import { createAssignedLabelsTableColumns } from './assignedLabelsTableColumns';
import type { EditableAssignedLabel } from './assignedLabelsTypes';
import { useAssignedLabelDeleteActions } from './useAssignedLabelDeleteActions';
import {
  SNAPSHOT_IN_PROGRESS_LABEL_MESSAGE,
  useIsSnapshotInProgress,
} from './useIsLatestSnapshot';

const assignedLabelsTableDescription =
  'Assign catalog labels to devices and interfaces so inventory can be filtered and checked by intent. Each assignment is stored globally and reapplied to new snapshots; it can be a direct (manual) assignment or inherited from a device label.';

const assignedLabelsRowSelectionFilter: RowSelectionFilter = {
  canRowBeSelected: (row) =>
    readTableCell((row as PostTablesLabels200DataItem).assignment) === 'manual',
  selectAllFilter: {
    assignment: ['eq', 'manual'],
  },
};

/**
 * A whole inheritance chain: the root assignment together with the rows that
 * inherited from it, which is the reach a link from another page hands over.
 */
const toChainFilters = (rootId: string) => ({
  rootId: ['eq', rootId] satisfies Filter,
});

/**
 * The root assignment on its own. `rootId` holds the bare id of an assignment,
 * the same one `labelId` exposes, while the `id` column prefixes it with the
 * target type, so the row is matched on `labelId`.
 */
const toRootFilters = (rootId: string) => ({
  labelId: ['eq', rootId] satisfies Filter,
});

export const useAssignedLabelsTable = (
  snapshotId: string,
  /** Root assignment handed over from a label source link. */
  initialRootId?: string,
) => {
  const isInProgress = useIsSnapshotInProgress(snapshotId);
  const disabledReason = isInProgress
    ? SNAPSHOT_IN_PROGRESS_LABEL_MESSAGE
    : undefined;
  const confirmDeleteDialog = useSimpleConfirmDialog();
  const [editedAssignedLabel, setEditedAssignedLabel] =
    useState<EditableAssignedLabel | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const apiTablePageRef = useRef<ReturnType<typeof useApiTablePage> | null>(
    null,
  );
  const openDiscoveryDataEnrichment = useOpenDiscoveryDataEnrichment();

  // the identity has to stay stable, the table re-applies the filters whenever
  // it changes, which would undo removing the filter by hand
  const initialFilters = useMemo(
    () =>
      initialRootId === undefined ? undefined : toChainFilters(initialRootId),
    [initialRootId],
  );

  const showRootAssignment = useStableCallback((rootId: string) => {
    const apiTableModel = apiTablePageRef.current?.apiTableModel;

    apiTableModel?.dispatch(applyExternalFilters, {
      filters: toRootFilters(rootId),
    });
    apiTableModel?.dispatch(reloadTableData);
  });

  /**
   * Manual roots stay on this tab, so the row that produced the clicked one is
   * filtered in place instead of navigating to the page this table lives on.
   * Only the root is left, as it carries the actions the clicked row lacks.
   */
  const openLabelSource = useStableCallback(
    (link: DiscoveryDataEnrichmentLink) => {
      if (link.tab === 'assignedLabels') {
        showRootAssignment(link.rootId);
        return;
      }

      openDiscoveryDataEnrichment?.(link);
    },
  );

  const reloadTable = useStableCallback(() => {
    apiTablePageRef.current?.apiTableModel.dispatch(reloadTableData);
  });

  const { requestDeleteAssignedLabel, requestDeleteAssignedLabels } =
    useAssignedLabelDeleteActions({
      confirmDeleteDialog,
      reloadTable,
      snapshotId,
    });

  const getSelectedAssignedLabelRows = useStableCallback(
    (selectedRows: Set<TableRowIdValue>) => {
      const data = (apiTablePageRef.current?.apiTableModel.data ??
        []) as PostTablesLabels200DataItem[];

      return data.filter((row) => {
        const rowId = readTableCell(row.id);
        return rowId !== undefined && selectedRows.has(rowId);
      });
    },
  );

  const columns = useMemo(
    () => [
      ...createAssignedLabelsTableColumns({
        disabledReason,
        onDelete: (row) => {
          void requestDeleteAssignedLabel(row);
        },
        onEdit: setEditedAssignedLabel,
        onOpenSource:
          openDiscoveryDataEnrichment === undefined
            ? undefined
            : openLabelSource,
      }),
    ],
    [disabledReason, openDiscoveryDataEnrichment, requestDeleteAssignedLabel],
  );

  const bulkActions = useMemo(
    (): TableBulkAction[] =>
      isInProgress
        ? []
        : [
            {
              key: 'delete',
              title: 'Delete',
              accent: 'negative',
              disableAutoDeselect: true,
              icon: 'fa-trash-can',
              onClick: async ({
                selectedRows,
                deselectAll,
              }: TableBulkActionClickEvent) => {
                const rows = getSelectedAssignedLabelRows(selectedRows);
                if (await requestDeleteAssignedLabels(rows)) {
                  deselectAll();
                }
              },
            },
          ],
    [isInProgress, requestDeleteAssignedLabels],
  );

  const menuItems = useMemo(
    (): TableActionsItem[] => [
      {
        standalone: true,
        elementWithKey: (
          <Tooltip content={disabledReason} key='addAssignedLabel'>
            <span>
              <Button
                dataTestId='assigned-labels-add-new'
                disabled={isInProgress}
                icon='fa-plus-circle'
                onClick={() => setFormOpen(true)}
                size='small'
                type='button'
                variant='outline'
              >
                Add new
              </Button>
            </span>
          </Tooltip>
        ),
      },
    ],
    [disabledReason, isInProgress],
  );

  const endpoint = useTableEndpoint({
    apiEndpoint: '/tables/labels',
    alternative: {
      id: 'assigned-labels',
      columns: ['...', ...columns],
    },
  });

  const apiTablePage = useApiTablePage({
    endpoint,
    initialFilters,
    additionalApiTableProps: {
      bulkActions,
      description: assignedLabelsTableDescription,
      disableTableRowDetail: true,
      menuItems,
      rowIdField: 'id',
      rowSelectionFilter: isInProgress
        ? { canRowBeSelected: () => false, disableSelectAll: true }
        : assignedLabelsRowSelectionFilter,
    },
  });

  apiTablePageRef.current = apiTablePage;

  return {
    apiTablePage,
    confirmDeleteDialog,
    editedAssignedLabel,
    formOpen,
    reloadTable,
    setEditedAssignedLabel,
    setFormOpen,
  };
};
